import React, { useCallback, useEffect, useMemo, useState, memo } from "react";
import { Row } from "react-bootstrap";
import instance, { apiEncryptRequest, decryptData } from "./../Api";
import "./Blog.css";
import Loader from "./common/Loader";
import { useNavigate } from "react-router-dom";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

// Build the navigation state for navigating to a blog article
const articleNavState = (article, category) => ({
  title:        article.title,
  date:         article.date,
  authImg:      article.author_image,
  authName:     article.author,
  categoryName: category?.category,
  blogData:     article.sections,
});

// ─── Shared sub-components ────────────────────────────────────────────────────

// Category heading + orange divider line
const CategoryHeading = memo(({ name, isMobile, onClick }) => (
  <>
    <h2 className={isMobile ? undefined : "hover-underline"}
      style={{ display: "inline-block", marginRight: 10, color: "#035189", fontSize: isMobile ? 16 : undefined }}
      onClick={onClick}>
      {name}
    </h2>
    <div style={{ display: "inline-block", width: "100%", verticalAlign: "middle", border: "3px solid #e97730", marginTop: isMobile ? undefined : -12 }} />
  </>
));

// Article card (grid layout) — used in mobile + web non-Lifestyle/Featured sections
const ArticleCard = memo(({ article, category, isMobile, navigate }) => (
  <div className="card" style={isMobile ? { width: 150, marginTop: -1 } : undefined}
    onClick={() => navigate("/blogmore", { state: articleNavState(article, category) })}>
    <img src={article.main_image} alt={article.title} className="card-image" />
    <div className="card-content" style={isMobile ? undefined : { fontFamily: "Lexend Deca" }}>
      {isMobile ? (
        <h3 className="card-title" style={{ fontSize: 15 }}>{article.title}</h3>
      ) : (
        <a href="#" className="article-title">{article.title}</a>
      )}
      <p className="card-author">{article.author}</p>
      {article.date_status !== 0 && (
        <p className="card-date" style={isMobile ? { fontSize: 13 } : undefined}>{article.date}</p>
      )}
    </div>
  </div>
));

// Featured sidebar article — used in mobile + web Featured section
const FeaturedArticle = memo(({ article, category, navigate }) => (
  <div className="sidebar-article"
    style={{ fontFamily: "Lexend Deca", fontWeight: 500, lineHeight: 2, overflowWrap: "normal" }}
    onClick={() => navigate("/blogmore", { state: articleNavState(article, category) })}>
    <img src={article.main_image} alt={article.title} />
    <div className="feat-article-details">
      <h4 className="hover-underline" style={{ color: "black" }}>{article.title}</h4>
      <p>{article.author} {article.date_status !== 0 && `• ${article.date}`}</p>
    </div>
  </div>
));

// Hero slider image + title/author
const HeroSlider = memo(({ currentSlide, currentIndex, isMobile, navigate }) => {
  if (!currentSlide) return null;
  return (
    <>
      <div className="slider-container">
        <img className="slide-animation" key={currentIndex} src={currentSlide.image} alt={currentSlide.title}
          style={{ width: "100%", height: "auto", cursor: "pointer" }}
          onClick={() => navigate("/blogmore", { state: { title: currentSlide.title, date: currentSlide.created_at, authImg: currentSlide.author_image, authName: currentSlide.author, blogId: currentSlide.blog_id } })} />
      </div>
      <h2 style={{ fontFamily: "Lexend Deca", fontWeight: 500, lineHeight: 2, overflowWrap: "normal", fontSize: isMobile ? 16 : undefined }}>
        {currentSlide.title}
      </h2>
      <span style={{ fontFamily: "Lexend Deca", fontWeight: 500, lineHeight: 2, overflowWrap: "normal", fontSize: isMobile ? 14 : undefined }}>
        {currentSlide.author}{"   "}{currentSlide.date}
      </span>
    </>
  );
});

// Search bar — same in mobile + web
const SearchBar = memo(({ inputVal, setInputVal, navigate, isMobile }) => {
  const inputStyle = { width: isMobile ? 200 : 300, padding: isMobile ? "7px 15px" : "10px 15px", border: "1px solid #7890a1", borderTopLeftRadius: 8, borderBottomLeftRadius: 8, outline: "none", fontSize: 16, backgroundColor: "#f5f8fa" };
  const btnStyle   = { padding: isMobile ? "8px 20px" : "10px 20px", backgroundColor: "#e97730", color: "#fff", border: "1px solid #e97730", borderLeft: "none", cursor: "pointer", fontSize: 16, borderTopRightRadius: 8, borderBottomRightRadius: 8 };
  return (
    <div className="subscription-container" style={isMobile ? undefined : { display: "flex", alignItems: "center", width: 605 }}>
      <input placeholder="" className={isMobile ? "subscription-input" : undefined} value={inputVal} onChange={(e) => setInputVal(e.target.value)} style={inputStyle} />
      <button style={btnStyle} onClick={() => navigate("/blogsearch", { state: { searchText: inputVal } })}>Search</button>
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const Blog = ({ isMobile }) => {
  const navigate = useNavigate();

  const [loading,      setLoading]      = useState(false);
  const [blogList,     setBlogList]     = useState(null);
  const [sliderList,   setSliderList]   = useState([]);
  const [inputVal,     setInputVal]     = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentSlide = sliderList[currentIndex];

  // ── Auto-advance slider ──────────────────────────────────────────────────
  useEffect(() => {
    if (!loading && sliderList.length > 0) {
      const id = setInterval(() => setCurrentIndex((i) => (i + 1) % sliderList.length), 3000);
      return () => clearInterval(id);
    }
  }, [loading, sliderList]);

  // ── Fetch blogs + slider ─────────────────────────────────────────────────
  // Fix: original called sliderApi() from ListBlogs() but never called
  // setLoading(false) in ListBlogs itself — loading was only cleared inside
  // sliderApi(). If sliderApi threw, loading stayed true forever.
  // Now both fetches run in parallel via Promise.all, and loading clears once.
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const enc = await apiEncryptRequest();
        const [blogRes, sliderRes] = await Promise.all([
          instance.get("/user/blog",   enc),
          instance.get("/user/slider", enc),
        ]);
        const blogData   = await decryptData(blogRes.data);
        const sliderData = await decryptData(sliderRes.data);
        setBlogList(blogData.blog);
        setSliderList(sliderData.slider || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // ── Category helpers ─────────────────────────────────────────────────────
  const navToCategory = useCallback((cat) =>
    navigate("/blogsearch", { state: { searchCategory: cat } }), [navigate]);

  const lifestyleCategories = useMemo(() =>
    Array.isArray(blogList) ? blogList.filter((c) => c.category === "Lifestyle") : [], [blogList]);

  const featuredCategories = useMemo(() =>
    Array.isArray(blogList) ? blogList.slice(0, 5).filter((c) => c.category === "Featured") : [], [blogList]);

  const otherCategories = useMemo(() =>
    Array.isArray(blogList) ? blogList.filter((c) => c.category !== "Lifestyle" && c.category !== "Featured") : [], [blogList]);

  // ── Shared category sections — defined once, used in mobile + web ─────────
  const lifestyleSection = (
    <div className="ai-category" style={isMobile ? { marginBottom: -50, marginTop: 15 } : undefined}>
      {lifestyleCategories.map((category, index) => (
        <div key={index} className="category-section" style={{ marginLeft: isMobile ? "1rem" : "2.2rem", width: isMobile ? undefined : "101%", marginTop: isMobile ? -20 : undefined }}>
          <CategoryHeading name={category.category} isMobile={isMobile} onClick={() => navToCategory(category.category)} />
          <div className={isMobile ? "card-grid" : "articles-list"} style={{ marginTop: -5 }}>
            {category.articles.map((article, idx) => (
              isMobile ? (
                <ArticleCard key={idx} article={article} category={category} isMobile navigate={navigate} />
              ) : (
                <div key={idx} className="article-card" onClick={() => navigate("/blogmore", { state: articleNavState(article, category) })}>
                  <img src={article.main_image} alt={article.title} className="latimage" />
                  <div className="article-details" style={{ fontFamily: "Lexend Deca" }}>
                    <a href="#" className="article-title">{article.title}</a>
                    <div className="article-meta"><span className="article-author" /></div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const otherSection = (
    <div className="ai-category" style={isMobile ? { marginTop: 60 } : undefined}>
      {otherCategories.map((category, index) => (
        <div key={index} className="category-section" style={{ marginLeft: isMobile ? "1rem" : "2rem", width: isMobile ? undefined : "101%", marginTop: isMobile ? undefined : 20 }}>
          <CategoryHeading name={category.category} isMobile={isMobile} onClick={() => navToCategory(category.category)} />
          <div className="card-grid" style={{ marginTop: -5 }}>
            {category.articles.map((article, idx) => (
              <ArticleCard key={idx} article={article} category={category} isMobile={isMobile} navigate={navigate} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const featuredSection = (
    <div className="ai-category" style={isMobile ? { marginTop: 20 } : undefined}>
      {featuredCategories.map((category, index) => (
        isMobile ? (
          <div key={index} className="category-section" style={{ marginLeft: 20 }}>
            <h3 style={{ color: "#035189", fontFamily: "Lexend Deca", fontSize: 16 }} onClick={() => navToCategory(category.category)}>
              {category.category}
            </h3>
            <div style={{ display: "inline-block", width: "100%", verticalAlign: "middle", border: "3px solid #e97730" }} />
            {category.articles.map((article, i) => (
              <FeaturedArticle key={i} article={article} category={category} navigate={navigate} />
            ))}
          </div>
        ) : (
          <aside key={index} className="sidebar" style={{ marginTop: -30 }}>
            <h3 className="hover-underline" style={{ color: "#035189", fontFamily: "Lexend Deca" }} onClick={() => navToCategory(category.category)}>
              {category.category}
            </h3>
            {category.articles.map((article, i) => (
              <FeaturedArticle key={i} article={article} category={category} navigate={navigate} />
            ))}
          </aside>
        )
      ))}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {loading && <div className="loading-overlay" style={{ zIndex: 999999999 }}><Loader isLoading={loading} /></div>}

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div style={{ width: "100% !important", paddingBottom: 10, justifyContent: "center" }}>
          <Row>
            <div className="container">
              <h1 className="titless" style={{ fontFamily: "Lexend Deca", fontSize: 18, textAlign: "left", marginLeft: 10 }}>
                Unlock the Power of Cloud Computing - Strategies, Expert Tips, Latest Trends
              </h1>
              <p style={{ fontSize: 13, fontFamily: "Lexend Deca", fontWeight: 500, marginLeft: 10 }}>
                Empowering Business Growth and Transformation Through Cloud Innovation, Strategic Insights and Future-Ready Solutions in the Digital Cloudscape
              </p>

              <SearchBar inputVal={inputVal} setInputVal={setInputVal} navigate={navigate} isMobile />

              <div className="author-info-container">
                <div className="divider" style={{ border: "none", borderTop: "2px solid #e97730" }} />
              </div>

              <Row>
                <div className="col-md-10">
                  <div className="main-article" style={{ marginLeft: -5 }}>
                    <HeroSlider currentSlide={currentSlide} currentIndex={currentIndex} isMobile navigate={navigate} />
                  </div>
                </div>
                {lifestyleSection}
                {otherSection}
                {featuredSection}
              </Row>
            </div>
          </Row>
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <div style={{ height: "100vh" }}>
          <Row>
            <div className="col-md-2" />
            <div className="col-md-8">
              <header className="header">
                <div className="header-container">
                  .
                  <div className="header-content">
                    <h1 style={{ fontFamily: "Lexend Deca", fontWeight: "bold", color: "#035189", lineHeight: 1.5, overflowWrap: "normal" }}>
                      Unlock the Power of Cloud Computing - Strategies, Expert Tips, Latest Trends
                    </h1>
                    <div className="subscription-line">
                      <p style={{ fontSize: 20, fontFamily: "Lexend Deca", fontWeight: 500 }}>
                        Empowering Business Growth and Transformation Through Cloud Innovation, Strategic Insights and Future-Ready Solutions in the Digital Cloudscape
                      </p>
                      <SearchBar inputVal={inputVal} setInputVal={setInputVal} navigate={navigate} isMobile={false} />
                    </div>
                  </div>
                  <div className="divider" style={{ border: "none", borderTop: "5px solid #e97730", marginTop: 10, width: "101%" }} />
                </div>
              </header>

              <div className="contents">
                <div className="main-article" style={{ marginTop: -50, marginLeft: "1.2rem" }}>
                  <HeroSlider currentSlide={currentSlide} currentIndex={currentIndex} isMobile={false} navigate={navigate} />
                </div>
                {featuredSection}
              </div>

              {lifestyleSection}
              {otherSection}
            </div>
            <div className="col-md-2" />
          </Row>
        </div>
      )}
    </div>
  );
};

export default Blog;